<template>
    <div class="gb">
        <!-- Header -->
        <div class="gb-head">
            <div class="gb-head-id">
                <span class="gb-icon">
                    <NuxtIcon name="mdi:book-open-page-variant" size="18" />
                </span>
                <span class="gb-head-text">
                    <h3 class="gb-title">{{ $t('artist.guestbook.title') }}</h3>
                    <p class="gb-sub">{{ $t('artist.guestbook.sub') }}</p>
                </span>
            </div>

            <!-- Connect Button (if not verified) -->
            <a
                v-if="!canPost && !loading"
                href="/api/auth/spotify"
                @click.prevent="loginWithSpotify"
                class="gb-connect"
            >
                <NuxtIcon name="mdi:spotify" size="18" />
                {{ $t('artist.guestbook.connect') }}
            </a>
        </div>

        <!-- Messages List -->
        <div class="gb-list">
            <div v-if="loading" class="flex justify-center py-8">
                 <NuxtIcon name="mdi:loading" size="32" class="animate-spin text-white/20" />
            </div>
            
            <div v-else-if="messages.length === 0" class="gb-empty">
                <NuxtIcon name="mdi:message-outline" size="32" />
                <p>{{ $t('artist.guestbook.empty') }}</p>
            </div>

            <div v-for="msg in messages" :key="msg.id" class="flex gap-4 p-4 bg-white/[0.02] border border-white/5 rounded-lg hover:bg-white/5 transition group">
                <div class="flex-shrink-0">
                    <img :alt="msg.user.name || msg.user.title || ''" :src="msg.user.image || '/images/default-avatar.svg'" class="w-10 h-10 rounded-full object-cover border border-white/10" />
                </div>
                <div class="flex-1 min-w-0">
                    <div class="flex items-center justify-between mb-1">
                        <h4 class="text-sm font-bold text-white group-hover:text-purple-400 transition-colors">{{ msg.user.name }}</h4>
                        <span class="text-[10px] text-text-muted font-mono">{{ formatDate(msg.createdAt) }}</span>
                    </div>
                    <p class="text-sm text-text-muted leading-relaxed break-words">{{ msg.content }}</p>
                </div>
            </div>
        </div>

        <!-- Input Form (if can post) -->
        <div v-if="canPost" class="p-6 border-t border-white/5 bg-white/[0.02]">
            <!-- Groovy Emojis -->
            <div class="flex flex-wrap gap-2 mb-3">
                <button 
                    v-for="emoji in groovyEmojis" 
                    :key="emoji"
                    @click="addEmoji(emoji)"
                    class="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-purple-500/20 hover:scale-110 active:scale-95 transition-all text-lg border border-white/5 hover:border-purple-500/30"
                    :title="$t('artist.guestbook.add_emoji')"
                >
                    {{ emoji }}
                </button>
            </div>

            <textarea 
                v-model="newMessage" 
                rows="3" 
                class="w-full bg-black/20 border border-white/10 rounded-xl p-4 text-white text-sm focus:outline-none focus:border-purple-500/50 transition-colors placeholder:text-text-muted/50 resize-none"
                :placeholder="$t('artist.guestbook.placeholder')"
                :disabled="posting"
            ></textarea>
            <div class="flex justify-between items-center mt-4">
                <span class="text-[10px] text-text-muted uppercase tracking-wider">{{ newMessage.length }}/500</span>
                <div class="flex items-center gap-4">
                    <span v-if="verificationError" class="text-red-400 text-[10px] uppercase font-bold tracking-wider">
                        {{ verificationError }}
                    </span>
                    <button 
                        @click="postMessage" 
                        :disabled="!newMessage.trim() || posting"
                        class="px-6 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold uppercase tracking-wider text-xs transition-all shadow-lg hover:shadow-purple-500/25"
                    >
                        {{ posting ? $t('artist.guestbook.posting') : $t('artist.guestbook.submit') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue'

const { t, locale } = useI18n()

const props = defineProps<{
    artistId: string
}>()

// Use standard auth user if `useUserSession` is not available or custom
// Use standard auth user
const { loggedIn, user } = useUserSession()

// State
const messages = ref<any[]>([])
const newMessage = ref('')
const posting = ref(false)
const verifying = ref(false)
const verificationError = ref<string | null>(null)

// Groovy Emojis
const groovyEmojis = ['💿', '🎹', '🎸', '🎷', '🎧', '🎵', '🎶', '🔥', '✨', '💫', '🍄', '🌈', '💃', '🤘', '✌️', '🕶️']

const addEmoji = (emoji: string) => {
    newMessage.value += emoji
}

// We treat `pending` from useFetch as initial loading state
const loading = computed(() => pending.value)

const verified = computed(() => {
    // If we have an error, we are not verified. 
    // If we are logged in with spotify, we assume verified initially until a post fails.
    return !verificationError.value
})

const canPost = computed(() => {
    return loggedIn.value && (user.value as any)?.spotify?.id
})

// Fetch Messages
const { data: msgs, pending, refresh } = await useFetch(`/api/public/guestbook/${props.artistId}`)

watch(msgs, (newVal) => {
    if (newVal) {
        messages.value = newVal as any[]
    }
}, { immediate: true })

const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(locale.value || undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

const loginWithSpotify = () => {
    const redirectUrl = window.location.href
    // In dev, we must use [::1] for auth to match Spotify whitelist and ensure cookies are set on the auth domain
    const isDev = window.location.port === '3000' || window.location.hostname === 'localhost'
    const authStartUrl = isDev ? 'http://[::1]:3000/api/auth/start' : '/api/auth/start'
    
    window.location.href = `${authStartUrl}?next=${encodeURIComponent(redirectUrl)}`
}

const postMessage = async () => {
    if (!newMessage.value.trim()) return
    
    posting.value = true
    verificationError.value = null
    
    try {
        await $fetch('/api/public/guestbook/post', {
            method: 'POST',
            body: {
                artistId: props.artistId,
                content: newMessage.value
            }
        })
        
        newMessage.value = ''
        await refresh() 
        if (msgs.value) messages.value = msgs.value as any[]
        ElMessage.success(t('artist.guestbook.posted'))
        
    } catch (err: any) {
        if (err.statusCode === 403) {
            verificationError.value = err.statusMessage || t('artist.guestbook.follow_required')
        } else {
             ElMessage.error(err.data?.message || t('artist.guestbook.failed'))
        }
    } finally {
        posting.value = false
    }
}

const verifyStatus = () => {
    verificationError.value = null
    // Ideally we could have a check status endpoint, 
    // but for now re-clicking "Sign Guestbook" is the way.
    // Or we trigger a re-check if we had one.
    // Let's just clear error so they can try again.
}
</script>

<style scoped>
/* Night-market grammar: the guestbook used to be a purple-gradient card
   with 32px of header padding, which read as a different product from
   the page around it. Same markup, same logic, the shop's tokens. */
.gb {
  border: 1px solid var(--cy-rim, rgba(255, 255, 255, 0.06));
  background: rgb(255 255 255 / 0.02);
  overflow: hidden;
}
.gb-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 12px 14px;
  border-bottom: 1px solid var(--cy-line, rgba(255, 255, 255, 0.06));
}
.gb-head-id { display: flex; align-items: center; gap: 10px; min-width: 0; }
.gb-icon {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--primary-channels) / 0.14);
  color: var(--primary);
}
.gb-head-text { min-width: 0; }
.gb-title {
  font-family: var(--cy-display, sans-serif);
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-main);
}
.gb-sub { font-size: 0.72rem; color: var(--text-dim); }

.gb-connect {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  min-height: 36px;
  background: #1DB954;
  color: #000;
  font-family: var(--cy-display, sans-serif);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-decoration: none;
  transition: filter 0.15s;
}
.gb-connect:hover { filter: brightness(1.1); }

.gb-list {
  max-height: 340px;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.gb-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 28px 12px;
  color: var(--text-dim);
  text-align: center;
}
.gb-empty p {
  font-family: var(--cy-mono, monospace);
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

@media (pointer: coarse) {
  .gb-connect { min-height: 44px; }
}
</style>
